import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

import DashboardOverview from "./DashboardOverview";
import PostsManager from "./PostsManager";
import GalleryManager from "./GalleryManager";
import HeroSlidesManager from "./HeroSlidesManager";
import StatsManager from "./StatsManager";
import SettingsManager from "./SettingsManager";
import MessagesManager from "./MessagesManager";
import NewsletterManager from "./NewsletterManager";
import UsersManager from "./UsersManager";

type AdminTab =
  | "overview"
  | "posts"
  | "gallery"
  | "hero"
  | "stats"
  | "settings"
  | "messages"
  | "newsletter"
  | "users";

const navItems: {
  key: AdminTab;
  label: string;
  icon: string;
  roles: ("super_admin" | "admin" | "editor")[];
}[] = [
  { key: "overview", label: "Dashboard", icon: "ri-dashboard-line", roles: ["super_admin", "admin", "editor"] },
  { key: "posts", label: "Posts", icon: "ri-article-line", roles: ["super_admin", "admin", "editor"] },
  { key: "gallery", label: "Gallery", icon: "ri-image-line", roles: ["super_admin", "admin", "editor"] },
  { key: "hero", label: "Hero Slides", icon: "ri-slideshow-line", roles: ["super_admin", "admin", "editor"] },
  { key: "stats", label: "Statistics", icon: "ri-bar-chart-box-line", roles: ["super_admin", "admin", "editor"] },
  { key: "settings", label: "Site Settings", icon: "ri-settings-3-line", roles: ["super_admin", "admin", "editor"] },
  { key: "messages", label: "Messages", icon: "ri-mail-line", roles: ["super_admin", "admin", "editor"] },
  { key: "newsletter", label: "Newsletter", icon: "ri-mail-send-line", roles: ["super_admin", "admin", "editor"] },
  { key: "users", label: "Users", icon: "ri-user-settings-line", roles: ["super_admin"] },
];

export default function AdminDashboard() {
  const { user, role, isAuthenticated, isLoading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Get active tab from URL
  const subPath = location.pathname.replace("/admin", "").replace("/", "");
  const [activeTab, setActiveTab] = useState<AdminTab>((subPath as AdminTab) || "overview");

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/secure-admin-login", { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]);

  // Redirect if role doesn't have access to the tab
  useEffect(() => {
    const item = navItems.find((n) => n.key === activeTab);
    if (item && role && !item.roles.includes(role)) {
      setActiveTab("overview");
      navigate("/admin", { replace: true });
    }
  }, [activeTab, role, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-2 border-primary-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  const handleTabChange = (tab: AdminTab) => {
    setActiveTab(tab);
    navigate(`/admin/${tab}`, { replace: true });
    setSidebarOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const visibleNav = navItems.filter((item) => role && item.roles.includes(role));

  const renderContent = () => {
    switch (activeTab) {
      case "overview":
        return <DashboardOverview />;
      case "posts":
        return <PostsManager />;
      case "gallery":
        return <GalleryManager />;
      case "hero":
        return <HeroSlidesManager />;
      case "stats":
        return <StatsManager />;
      case "settings":
        return <SettingsManager />;
      case "messages":
        return <MessagesManager />;
      case "newsletter":
        return <NewsletterManager />;
      case "users":
        return <UsersManager />;
      default:
        return <DashboardOverview />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-primary-900 text-white transition-transform duration-300 lg:translate-x-0 lg:static lg:flex-shrink-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="flex items-center gap-3 px-6 py-5 border-b border-white/10">
            <img
              src="https://static.readdy.ai/image/cb5bba3f8fb0d3000da0c3fb91699bfa/a250bf31368e0cf0c4367263661697d1.jpeg"
              alt="Logo"
              className="h-10 w-10 rounded-full object-cover"
            />
            <div>
              <p className="font-heading font-bold text-sm">Admin Panel</p>
              <p className="text-xs text-white/50">ACS Student Chapter</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {visibleNav.map((item) => (
              <button
                key={item.key}
                onClick={() => handleTabChange(item.key)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === item.key
                    ? "bg-white/10 text-gold-400"
                    : "text-white/70 hover:text-white hover:bg-white/5"
                }`}
              >
                <i className={`${item.icon} text-base`} />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          {/* Sidebar Footer */}
          <div className="px-4 py-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-gold-500/20 flex items-center justify-center text-gold-400">
                <i className="ri-user-line" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {user?.user_metadata?.name || user?.email}
                </p>
                <p className="text-xs text-white/50 capitalize">{role?.replace("_", " ")}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/70 hover:text-red-400 hover:bg-red-400/10 transition-all"
            >
              <i className="ri-logout-box-r-line" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-4 md:px-6 h-16 flex items-center justify-between lg:justify-end sticky top-0 z-20">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600"
            aria-label="Open sidebar"
          >
            <i className="ri-menu-line text-xl" />
          </button>

          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500 hidden md:inline">
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
              })}
            </span>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gold-50 flex items-center justify-center text-gold-600">
                <i className="ri-user-line text-sm" />
              </div>
              <span className="text-sm font-medium text-gray-700 hidden sm:inline">
                {user?.user_metadata?.name || "Admin"}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}